import {
  Card,
  CardFooter,
  CardFooterProps as ChakraUICardFooterProps,
  Flex,
  HStack,
  Stack,
  useBreakpointValue,
  Skeleton
} from "@chakra-ui/react";
import {
  FiMessageCircle,
  FiThumbsUp,
  FiThumbsDown,
  FiShare2
} from "react-icons/fi";

import { Tags } from "./tags/tags";
import { FooterIcon } from "./footer-icon/footer-icon";
import { SocialShare } from "@/storybook-components/social-share/social-share";

export interface CardFooterProps extends ChakraUICardFooterProps {
  commentsCount?: string | number;
  thumbsUpCount?: string | number;
  thumbsDownCount?: string | number;
  shareText?: string;
  showCommentIcon?: boolean;
  isThumbsUp?: boolean;
  isThumbsDown?: boolean;
  isShareIcon?: boolean;
  hasFooterHighlight?: boolean;
  ariaLabelCommentIcon?: string;
  ariaLabelVoteIcon?: string;
  ariaLabelThumbsDownIcon?: string;
  ariaLabelShareIcon?: string;
  showTags?: boolean;
  tags?: Array<string>;
  handleComment?(): any;
  handleVoteUp?(): any;
  handleVoteDown?(): any;
  handleShare?(): any;
  isLoaded?: boolean;
  cardVariant?: string;
  isVoteActive?: boolean;
  isVoteDisabled?: boolean;
  isIconButtonPressed?: boolean;
  makeFooterViewOnly?: boolean;
  socialShareTitle?: string;
  socialShareUrl?: string;
}

export function Footer(props: CardFooterProps) {
  const {
    commentsCount,
    thumbsUpCount,
    thumbsDownCount,
    shareText,
    showCommentIcon,
    isThumbsUp,
    isThumbsDown,
    isShareIcon,
    showTags,
    tags,
    handleComment,
    handleVoteUp,
    handleVoteDown,
    handleShare,
    ariaLabelCommentIcon,
    ariaLabelVoteIcon,
    isIconButtonPressed,
    ariaLabelThumbsDownIcon,
    ariaLabelShareIcon,
    isLoaded,
    hasFooterHighlight,
    cardVariant,
    isVoteActive,
    isVoteDisabled,
    makeFooterViewOnly,
    socialShareTitle,
    socialShareUrl
  } = props;

  const showShareText = useBreakpointValue({
    base: true,
    md: false
  });

  return (
    <Card
      boxShadow="none"
      bgColor={cardVariant === "elevated" ? "whiteAlpha.100" : "transparent"}
    >
      <CardFooter
        p={cardVariant === "unstyled" ? 0 : 2}
        pt={0}
        flexWrap="wrap"
        flexDirection="column"
        className={hasFooterHighlight ? "" : "no-highlight"}
        pointerEvents={makeFooterViewOnly ? "none" : "auto"}
      >
        <Skeleton isLoaded={isLoaded}>
          <Stack gap={showTags && tags ? 2 : 4}>
            {showTags && tags && <Tags tags={tags} />}
            <HStack justify="space-between">
              <Flex gap={2} justify="flex-start" alignItems="center">
                <Flex alignItems="center">
                  {isThumbsUp && (
                    <Flex alignItems="center">
                      <FooterIcon
                        iconText={thumbsUpCount}
                        icon={FiThumbsUp}
                        onClick={handleVoteUp}
                        iconAriaLabel={ariaLabelVoteIcon}
                        isIconButtonPressed={isIconButtonPressed}
                        testIdFooterIcon="card-thumbsUpIcon"
                        testIdCountText="card-thumbsUpCountText"
                        isVoteActive={isVoteActive}
                        isVoteDisabled={isVoteDisabled}
                      />
                    </Flex>
                  )}
                  {showCommentIcon && (
                    <FooterIcon
                      iconText={commentsCount}
                      icon={FiMessageCircle}
                      onClick={handleComment}
                      iconAriaLabel={ariaLabelCommentIcon}
                      testIdFooterIcon="card-commentIcon"
                      testIdCountText="card-commentsCountText"
                    />
                  )}
                </Flex>
                {isThumbsDown && (
                  <Flex alignItems="center">
                    <FooterIcon
                      iconText={thumbsDownCount}
                      icon={FiThumbsDown}
                      onClick={handleVoteDown}
                      iconAriaLabel={ariaLabelThumbsDownIcon}
                      testIdFooterIcon="card-thumbsDownIcon"
                      testIdCountText="card-thumbsDownCountText"
                    />
                  </Flex>
                )}
              </Flex>
              {isShareIcon && (
                <Flex justify="flex-start" alignItems="center">
                  {socialShareTitle && socialShareUrl ? (
                    <SocialShare
                      placement="left"
                      title={socialShareTitle}
                      shareUrl={socialShareUrl}
                      trigger={
                        <FooterIcon
                          iconText={shareText}
                          icon={FiShare2}
                          onClick={handleShare}
                          iconAriaLabel={ariaLabelShareIcon}
                          hideIconText={showShareText}
                          testIdFooterIcon="card-shareIcon"
                          testIdCountText="card-shareText"
                        />
                      }
                    />
                  ) : (
                    <FooterIcon
                      iconText={shareText}
                      icon={FiShare2}
                      onClick={handleShare}
                      iconAriaLabel={ariaLabelShareIcon}
                      hideIconText={showShareText}
                      testIdFooterIcon="card-shareIcon"
                      testIdCountText="card-shareText"
                    />
                  )}
                </Flex>
              )}
            </HStack>
          </Stack>
        </Skeleton>
      </CardFooter>
    </Card>
  );
}
